import { Query, TCalendarEvent, TCalendarUrls } from "./types.ts";

import { DateTime } from "luxon";

/* https://github.com/vborodulin/add-event-to-calendar */

const makeUrl = (base: string, query: Query) =>
  Object.keys(query).reduce((accum, key, index) => {
    const value = query[key];

    if (value !== null) {
      return `${accum}${index === 0 ? "?" : "&"}${key}=${encodeURIComponent(value)}`;
    }
    return accum;
  }, base);

  const getDefaultTimeZone =()=>{
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (error) {
    return "UTC";
  }
}

const makeGoogleCalendarUrl = (event: TCalendarEvent) => {
  const isAllDay = event.allDay;
  const tz = event.timezone || getDefaultTimeZone();

  const start = DateTime.fromISO(event.startsAt, { zone: tz });
  let end = DateTime.fromISO(event.endsAt, { zone: tz });

  let dates: string;
  if (isAllDay) {
    // Google excluye el día final, así que sumamos 1
    end = end.plus({ days: 1 });
    dates = `${start.toFormat("yyyyLLdd")}/${end.toFormat("yyyyLLdd")}`;
  } else {
    dates = `${start.toUTC().toFormat("yyyyLLdd'T'HHmmss'Z'")}/${end.toUTC().toFormat("yyyyLLdd'T'HHmmss'Z'")}`;
  }

  return makeUrl("https://calendar.google.com/calendar/render", {
    action: "TEMPLATE",
    dates,
    location: event.location,
    text: event.name,
    details: event.details,
    ctz: tz,
  });
};

const makeTimeOutlook = (time: string, timezone:string) => {
  if (!time) return "";
  return DateTime.fromISO(time, [{timezone}]).toUTC().toFormat("yyyy-LL-dd'T'HH:mm:ss'Z'");
};

const makeOutlookCalendarUrl = (event: TCalendarEvent) =>{
  const tz = event.timezone || getDefaultTimeZone();
  return makeUrl("https://outlook.live.com/owa", {
    rru: "addevent",
    startdt: makeTimeOutlook(event.startsAt, tz),
    enddt: makeTimeOutlook(event.endsAt, tz),
    subject: event.name,
    location: event.location,
    body: event.details,
    allday: false,
    uid: new Date().getTime().toString(),
    path: "/calendar/view/Month",
  })}


const makeYahooTime = (time: string, timezone: string) => {
  return DateTime.fromISO(time, { zone: timezone }).toFormat("yyyyLLdd'T'HHmmss");
};

const makeYahooDuration = (event: TCalendarEvent) => {
  const start = DateTime.fromISO(event.startsAt, { zone: event.timezone });
  const end = DateTime.fromISO(event.endsAt, { zone: event.timezone });

  const diff = end.diff(start, ["hours", "minutes"]).toObject();
  const hours = Math.floor(diff.hours || 0);
  const minutes = Math.floor(diff.minutes || 0);

  return `${hours.toString().padStart(2, "0")}${minutes.toString().padStart(2, "0")}`;
};

const makeYahooCalendarUrl = (event: TCalendarEvent) => {
  const tzid = event.timezone || getDefaultTimeZone();
  const start = makeYahooTime(event.startsAt,tzid);
  const duration = makeYahooDuration(event);

  const params = new URLSearchParams({
    v: "60",
    view: "d",
    type: "20",
    title: event.name,
    st: start,
    dur: duration,
    desc: event.details,
    in_loc: event.location,
  });

  return `https://calendar.yahoo.com/?${params.toString()}`;
};


const makeTimeICS = (time: string, timezone: string) => {
  const dt = DateTime.fromISO(time).setZone(timezone);
  return dt.toFormat("yyyyLLdd'T'HHmmss");
};

const makeVTimezone = (timezone: string) => {
  const dt = DateTime.now().setZone(timezone);
  const offset = dt.offset; // minutos de offset actual respecto a UTC
  const offsetSign = offset >= 0 ? "+" : "-";

  // convertir minutos a formato ±HHMM
  const pad = (n: number) => n.toString().padStart(2, "0");
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);
  const tzOffset = `${offsetSign}${offsetHours}${offsetMinutes}`;

  return [
    "BEGIN:VTIMEZONE",
    `TZID:${timezone}`,
    "BEGIN:STANDARD",
    `TZOFFSETFROM:${tzOffset}`,
    `TZOFFSETTO:${tzOffset}`,
    `TZNAME:${timezone}`,
    `DTSTART:19700101T000000`,
    "END:STANDARD",
    "END:VTIMEZONE",
  ].join("\n");
};

const makeICSCalendarUrl = (event: TCalendarEvent) => {
  const tzid = event.timezone || getDefaultTimeZone();

  const components = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    makeVTimezone(tzid),
    "BEGIN:VEVENT",
    `URL:${document.URL}`,
    `DTSTART;TZID=${tzid}:${makeTimeICS(event.startsAt, tzid)}`,
    `DTEND;TZID=${tzid}:${makeTimeICS(event.endsAt, tzid)}`,
    `SUMMARY:${event.name}`,
    `DESCRIPTION:${event.details}`,
    `LOCATION:${event.location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return encodeURI(`data:text/calendar;charset=utf8,${components.join("\n")}`);
};

const makeUrls = (event: TCalendarEvent): TCalendarUrls => ({
  google: makeGoogleCalendarUrl(event),
  outlook: makeOutlookCalendarUrl(event),
  yahoo: makeYahooCalendarUrl(event),
  ics: makeICSCalendarUrl(event),
});

export default makeUrls;
